"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { ECharts, EChartsCoreOption } from "echarts";
import { useTheme } from "next-themes";
import styles from "./chart-antd.module.css";
import { resolveHashrateDisplayScale } from "./hashrate-display-scale";
import { UNIFIED_CHART_WRAPPER_CLASS } from "./line-tooltip-behavior";
import { IDomainPoolStatHistories } from "@/data/domain/data";
import { useWindowSize } from "@/hooks/window-size";

const MOBILE_VIEWPORT_MAX_WIDTH = 768;
const MAIN_CHART_POINTS_LIMIT = 70;
const MOBILE_VIEW_WINDOW_POINTS = 35;
const APACHE_LINE_COLOR = "#5B8FF9";

type ITooltipAxisParam = {
  axisValueLabel?: string;
  name?: string;
  marker?: string;
  seriesName?: string;
  value?: unknown;
  data?: unknown;
};

function hexToRgba(hexColor: string, alpha: number): string {
  const normalized = String(hexColor ?? "").replace("#", "");
  if (normalized.length != 6) {
    return `rgba(91, 143, 249, ${alpha})`;
  }

  const red = Number.parseInt(normalized.slice(0, 2), 16);
  const green = Number.parseInt(normalized.slice(2, 4), 16);
  const blue = Number.parseInt(normalized.slice(4, 6), 16);
  if (![red, green, blue].every((value) => Number.isFinite(value))) {
    return `rgba(91, 143, 249, ${alpha})`;
  }

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function provideLineAreaGradient(lineColor: string) {
  return {
    type: "linear" as const,
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: hexToRgba(lineColor, 0.5) },
      { offset: 1, color: hexToRgba(lineColor, 0) },
    ],
  };
}

function roundTo3(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Number(value.toFixed(3));
}

function formatAxisYLabel(raw: number | string): string {
  const value = Number(raw);
  if (!Number.isFinite(value)) return String(raw);
  return value.toFixed(3).replace(/\.?0+$/, "");
}

function provideThemeColors(theme: string | undefined): {
  text: string;
  grid: string;
} {
  if (theme == "dark") {
    return { text: "#fff", grid: "rgba(255,255,255,0.15)" };
  }
  return { text: "#000", grid: "rgba(0,0,0,0.15)" };
}

function provideSeriesData(
  values: IDomainPoolStatHistories | undefined,
  scaleFactor = 1
): Array<[number, number]> {
  const source = [...(values?.histories ?? [])]
    .sort((a, b) => a.x.getTime() - b.x.getTime())
    .slice(-MAIN_CHART_POINTS_LIMIT);

  return source.map((point) => [point.x.getTime(), roundTo3(point.y * scaleFactor)]);
}

function provideMobileWindowStartPercent(pointsCount: number): number {
  if (!Number.isFinite(pointsCount) || pointsCount <= MOBILE_VIEW_WINDOW_POINTS) {
    return 0;
  }

  return ((pointsCount - MOBILE_VIEW_WINDOW_POINTS) / pointsCount) * 100;
}

function provideRefreshKey(values: IDomainPoolStatHistories | undefined): string {
  const source = values?.histories ?? [];
  if (source.length == 0) return `${values?.suffix ?? ""}|0`;

  const first = source[0];
  const last = source[source.length - 1];
  return `${values?.suffix ?? ""}|${source.length}|${first.x.getTime()}|${last.x.getTime()}|${roundTo3(last.y)}`;
}

function resolveTooltipNumericValue(param: ITooltipAxisParam): number | undefined {
  const directValue = Array.isArray(param.value)
    ? param.value[param.value.length - 1]
    : param.value;
  const fallbackValue = Array.isArray(param.data)
    ? param.data[param.data.length - 1]
    : param.data;
  const raw = directValue ?? fallbackValue;
  const value = Number(raw);
  if (!Number.isFinite(value)) return undefined;
  return value;
}

function formatAxisTooltip(params: unknown, suffix: string): string {
  const points = (Array.isArray(params) ? params : [params]) as ITooltipAxisParam[];
  if (points.length == 0) return "";

  const title = points[0]?.axisValueLabel ?? points[0]?.name ?? "";
  const suffixText = suffix.length > 0 ? ` ${suffix}` : "";
  const lines = points
    .map((point) => {
      const value = resolveTooltipNumericValue(point);
      const labelPrefix =
        point.seriesName != undefined && point.seriesName.length > 0
          ? `${point.seriesName}: `
          : "";
      const valueText = value == undefined ? "-" : formatAxisYLabel(value);
      return `${point.marker ?? ""}${labelPrefix}${valueText}${suffixText}`;
    })
    .join("<br/>");

  return title.length > 0 ? `${title}<br/>${lines}` : lines;
}

export function MainChart({
  w,
  values,
}: {
  w: number;
  values: IDomainPoolStatHistories | undefined;
}) {
  const { theme } = useTheme();
  const windowSize = useWindowSize();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | undefined>(undefined);
  const [chartReadyToken, setChartReadyToken] = useState(0);
  const isMobile =
    windowSize.width > 0
      ? windowSize.width <= MOBILE_VIEWPORT_MAX_WIDTH
      : false;
  const chartHeight = isMobile ? 360 : 450;
  const refreshKey = useMemo(() => provideRefreshKey(values), [values]);
  const colors = useMemo(() => provideThemeColors(theme), [theme]);
  const sourceSuffix = useMemo(
    () => String(values?.suffix ?? "").trim(),
    [values?.suffix]
  );
  const scaleMeta = useMemo(
    () =>
      resolveHashrateDisplayScale(
        (values?.histories ?? []).map((point) => Number(point.y)),
        sourceSuffix
      ),
    [sourceSuffix, values?.histories]
  );
  const seriesData = useMemo(
    () => provideSeriesData(values, scaleMeta.factor),
    [scaleMeta.factor, values]
  );
  const tooltipSuffix = scaleMeta.suffix;
  const yAxisTitle = useMemo(() => {
    if (isMobile) return "";
    return scaleMeta.suffix;
  }, [isMobile, scaleMeta.suffix]);
  const mobileWindowStartPercent = useMemo(
    () => provideMobileWindowStartPercent(seriesData.length),
    [seriesData.length]
  );

  useEffect(() => {
    if (!isMobile) return;
    const chart = chartRef.current;
    const container = containerRef.current;
    if (chart == undefined || container == null) return;

    let hideTipTimeoutId: number | undefined;
    const clearHideTipTimeout = () => {
      if (hideTipTimeoutId == undefined) return;
      window.clearTimeout(hideTipTimeoutId);
      hideTipTimeoutId = undefined;
    };
    const hideTip = () => {
      clearHideTipTimeout();
      chart.dispatchAction({ type: "hideTip" });
    };
    const scheduleHideTip = () => {
      clearHideTipTimeout();
      hideTipTimeoutId = window.setTimeout(() => {
        hideTipTimeoutId = undefined;
        chart.dispatchAction({ type: "hideTip" });
      }, 5000);
    };
    const handleTapOutsideChart = (event: Event) => {
      const targetNode = event.target;
      if (!(targetNode instanceof Node)) return;
      if (container.contains(targetNode)) return;
      hideTip();
    };
    const handleTapOnChart = () => {
      scheduleHideTip();
    };

    chart.getZr().on("click", handleTapOnChart);
    document.addEventListener("pointerdown", handleTapOutsideChart);
    document.addEventListener("touchstart", handleTapOutsideChart, {
      passive: true,
    });

    return () => {
      clearHideTipTimeout();
      chart.getZr().off("click", handleTapOnChart);
      document.removeEventListener("pointerdown", handleTapOutsideChart);
      document.removeEventListener("touchstart", handleTapOutsideChart);
    };
  }, [chartReadyToken, isMobile, refreshKey]);

  useEffect(() => {
    let disposed = false;
    let resizeObserver: ResizeObserver | undefined;
    let detachWindowResize: (() => void) | undefined;

    const bootstrap = async () => {
      const echarts = await import("echarts");
      if (disposed) return;
      if (containerRef.current == null) return;

      chartRef.current?.dispose();
      const chart = echarts.init(containerRef.current, undefined, {
        renderer: "canvas",
      });
      chartRef.current = chart;
      setChartReadyToken((prev) => prev + 1);

      const onResize = () => {
        chartRef.current?.resize();
      };

      if (typeof ResizeObserver != "undefined") {
        resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(containerRef.current);
      } else {
        window.addEventListener("resize", onResize);
        detachWindowResize = () => {
          window.removeEventListener("resize", onResize);
        };
      }
    };

    void bootstrap();

    return () => {
      disposed = true;
      resizeObserver?.disconnect();
      detachWindowResize?.();
      chartRef.current?.dispose();
      chartRef.current = undefined;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (chart == undefined) return;

    const option: EChartsCoreOption = {
      animation: true,
      grid: {
        left: isMobile ? 15 : 36,
        right: isMobile ? 12 : 6,
        top: 10,
        bottom: isMobile ? 28 : 28,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params: unknown) => formatAxisTooltip(params, tooltipSuffix),
      },
      dataZoom: isMobile
        ? [
            {
              type: "inside",
              xAxisIndex: 0,
              start: mobileWindowStartPercent,
              end: 100,
              zoomLock: false,
              moveOnMouseMove: true,
              zoomOnMouseWheel: false,
              preventDefaultMouseMove: false,
            },
            {
              type: "slider",
              xAxisIndex: 0,
              start: mobileWindowStartPercent,
              end: 100,
              brushSelect: false,
              height: 20,
              bottom: 8,
              borderColor: "transparent",
              fillerColor:
                theme == "dark"
                  ? "rgba(22, 119, 255, 0.28)"
                  : "rgba(22, 119, 255, 0.2)",
              backgroundColor:
                theme == "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.08)",
              handleStyle: {
                color: theme == "dark" ? "#fff" : "#1677ff",
                borderColor: "transparent",
              },
              showDetail: false,
              showDataShadow: true,
            },
          ]
        : undefined,
      xAxis: {
        type: "time",
        splitNumber: isMobile ? 2 : 4,
        axisLine: {
          lineStyle: {
            color: colors.grid,
            type: "dashed",
          },
        },
        axisLabel: {
          color: colors.text,
          // rotate: isMobile ? 90 : 0,
          hideOverlap: true,
          margin: isMobile ? 11 : 8,
          interval: 1,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: colors.grid,
            type: "dashed",
          },
        },
      },
      yAxis: {
        type: "value",
        name: yAxisTitle,
        nameLocation: "middle",
        nameRotate: 90,
        nameGap: isMobile ? 20 : 34,
        nameTextStyle: {
          color: colors.text,
        },
        axisLine: {
          lineStyle: {
            color: colors.text,
          },
        },
        axisLabel: {
          color: colors.text,
          formatter: (raw: number | string) => formatAxisYLabel(raw),
        },
        splitLine: {
          lineStyle: {
            color: colors.grid,
            type: "dashed",
          },
        },
      },
      series: [
        {
          type: "line",
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 3,
            color: APACHE_LINE_COLOR,
          },
          areaStyle: {
            color: provideLineAreaGradient(APACHE_LINE_COLOR),
          },
          connectNulls: true,
          data: seriesData,
        },
      ],
    };

    chart.setOption(
      option,
      true
    );

    const frame = window.requestAnimationFrame(() => {
      chart.resize();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    chartReadyToken,
    colors.grid,
    colors.text,
    isMobile,
    refreshKey,
    seriesData,
    mobileWindowStartPercent,
    theme,
    tooltipSuffix,
    yAxisTitle,
  ]);

  return (
    <div
      className={`${styles.chartWrapper} ${UNIFIED_CHART_WRAPPER_CLASS}`}
      style={{ minHeight: chartHeight, width: "100%", maxWidth: `${w}px` }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: chartHeight }}
      />
    </div>
  );
}
